import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../../context/UserContext';
import { canAccessAgencyAdmin } from '../../utils/roleUtils';
import AdminPageHeader from '../../components/admin/AdminPageHeader';
import AdminPanel from '../../components/admin/AdminPanel';
import AdminEmptyState from '../../components/admin/AdminEmptyState';
import agencyAdminService from '../../services/agencyAdminService';
import { 
  AlertCircle, 
  FileText,
  Calendar,
  RefreshCw,
  ArrowRight,
  AlertTriangle
} from 'lucide-react';
import '../../styles/admin-theme.css';

const AttentionInbox = () => {
  const navigate = useNavigate();
  const { user } = useUser();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [attentionItems, setAttentionItems] = useState([]);
  const [filterType, setFilterType] = useState('all');

  const [allItems, setAllItems] = useState([]);

  useEffect(() => {
    if (user && !canAccessAgencyAdmin(user.role)) {
      navigate('/agent/dashboard');
      return;
    }
    if (user) {
      fetchAttentionItems();
    }
  }, [user, navigate]);

  // Check if user has agency admin access
  const hasAgencyAdminAccess = user && canAccessAgencyAdmin(user.role);

  useEffect(() => {
    // Filter items when filterType changes
    if (filterType === 'all') {
      setAttentionItems(allItems);
    } else {
      setAttentionItems(allItems.filter(item => item.type === filterType));
    }
  }, [filterType, allItems]);

  const fetchAttentionItems = async (refresh = false) => {
    try {
      if (refresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);

      const response = await agencyAdminService.getDashboardSummary();
      const data = response.data || response;
      
      // Guard against missing attentionRequired structure
      const items = (data.attentionRequired && Array.isArray(data.attentionRequired.items)) 
        ? data.attentionRequired.items 
        : [];
      setAllItems(items);
      
      // Apply current filter
      if (filterType === 'all') {
        setAttentionItems(items);
      } else {
        setAttentionItems(items.filter(item => item.type === filterType));
      }

    } catch (err) {
      console.error('Error fetching attention items:', err);
      setError(err.message || 'Failed to load attention items');
      setAttentionItems([]);
      setAllItems([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Get attention item icon
  const getAttentionIcon = (type) => {
    switch (type) {
      case 'TRIP_STALLED':
        return AlertCircle;
      case 'FORM_ABANDONED':
        return FileText;
      case 'NO_ITINERARY':
        return Calendar;
      default:
        return AlertCircle;
    }
  };

  // Get attention item color classes
  const getAttentionColorClasses = (type) => {
    switch (type) {
      case 'TRIP_STALLED':
        return {
          bg: 'bg-red-100',
          text: 'text-red-600',
        };
      case 'FORM_ABANDONED':
        return {
          bg: 'bg-orange-100',
          text: 'text-orange-600',
        };
      case 'NO_ITINERARY':
        return {
          bg: 'bg-blue-100',
          text: 'text-blue-600',
        };
      default:
        return {
          bg: 'bg-gray-100',
          text: 'text-gray-600',
        };
    }
  };

  // Get type label
  const getTypeLabel = (type) => {
    switch (type) {
      case 'TRIP_STALLED':
        return 'Stalled Trips';
      case 'FORM_ABANDONED':
        return 'Abandoned Forms';
      case 'NO_ITINERARY':
        return 'No Itinerary';
      default:
        return type;
    }
  };

  if (!hasAgencyAdminAccess) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <div className="text-center">
          <AlertCircle className="mx-auto mb-4 w-16 h-16 text-red-500" />
          <h2 className="mb-2 text-2xl font-bold text-gray-900">Access Denied</h2>
          <p className="text-gray-600">You don't have permission to access this page.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-center">
          <div className="admin-spinner mx-auto mb-4"></div>
          <p className="text-gray-600">Loading attention items...</p>
        </div>
      </div>
    );
  }

  const headerActions = [
    <button
      key="refresh"
      onClick={() => fetchAttentionItems(true)}
      disabled={refreshing}
      className="flex items-center px-3 py-2 text-sm text-gray-700 bg-white rounded-lg border border-gray-300 transition-colors hover:bg-gray-50 disabled:opacity-50"
    >
      <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
      Refresh
    </button>,
  ];

  const filterTabs = [
    { value: 'all', label: 'All Items' },
    { value: 'TRIP_STALLED', label: 'Stalled Trips' },
    { value: 'FORM_ABANDONED', label: 'Abandoned Forms' },
    { value: 'NO_ITINERARY', label: 'No Itinerary' },
  ];

  const filteredCounts = {
    all: allItems.length,
    TRIP_STALLED: allItems.filter(item => item.type === 'TRIP_STALLED').length,
    FORM_ABANDONED: allItems.filter(item => item.type === 'FORM_ABANDONED').length,
    NO_ITINERARY: allItems.filter(item => item.type === 'NO_ITINERARY').length,
  };

  return (
    <>
      <AdminPageHeader
        title="Attention Inbox"
        subtitle="Items requiring your attention"
        actions={headerActions}
      />

      {error && (
        <AdminPanel
          variant="bordered"
          className="mb-6 border-red-200 bg-red-50"
        >
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 mr-3 text-red-600" />
            <div>
              <h3 className="text-sm font-semibold text-red-900">Error Loading Items</h3>
              <p className="text-xs text-red-700">{error}</p>
            </div>
          </div>
        </AdminPanel>
      )}

      {/* Filter Tabs */}
      <AdminPanel className="mb-6">
        <div className="flex flex-wrap gap-2">
          {filterTabs.map((tab) => (
            <button
              key={tab.value}
              onClick={() => setFilterType(tab.value)}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                filterType === tab.value
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {tab.label} ({filteredCounts[tab.value] || 0})
            </button>
          ))}
        </div>
      </AdminPanel>

      {/* Attention Items List */}
      <AdminPanel title={`${getTypeLabel(filterType)} (${attentionItems.length})`}>
        {attentionItems.length > 0 ? (
          <div className="space-y-3">
            {attentionItems.map((item) => {
              const Icon = getAttentionIcon(item.type);
              const colorClasses = getAttentionColorClasses(item.type);
              return (
                <div
                  key={item.id}
                  onClick={() => item.targetUrl && navigate(item.targetUrl)}
                  className={`flex items-center p-4 space-x-3 rounded-lg transition-colors border ${
                    item.targetUrl ? 'cursor-pointer hover:bg-gray-50 border-gray-200' : 'border-gray-200'
                  }`}
                >
                  <div className={`flex-shrink-0 p-2 ${colorClasses.bg} rounded-lg`}>
                    <Icon className={`w-5 h-5 ${colorClasses.text}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">{item.message}</p>
                    {item.count !== undefined && (
                      <p className="text-xs text-gray-500 mt-1">
                        {item.count} item{item.count !== 1 ? 's' : ''}
                      </p>
                    )}
                  </div>
                  {item.targetUrl && (
                    <ArrowRight className="flex-shrink-0 w-5 h-5 text-gray-400" />
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <AdminEmptyState
            icon={AlertCircle}
            title="No items found"
            description={
              filterType === 'all'
                ? "All clear! No items require attention at this time."
                : `No ${getTypeLabel(filterType).toLowerCase()} found.`
            }
          />
        )}
      </AdminPanel>
    </>
  );
};

export default AttentionInbox;
